import { FormFieldSelect } from "components/FormFieldSelect";
import { ReactNode } from "react";
import { useAppSelector } from "redux/hooks";
import { selectReviewCurrentCancerStageOptions, selectSectionOptions } from "redux/slices/case";
import { LensHitFilterState } from "../LensHitFilterState";
import Flex from "components/Flex";
import { Box } from "@mui/material";
import { ClusterType, Form, PageType, ReviewConfig } from "generated/graphql";
import { nonEmpty } from "helpers/coll";
import { displayClusterType, displayPageType } from "Backend";
import FormFieldPopover from "components/FormField/FormFieldPopover";
import InfoIcon from "@mui/icons-material/Info";
import { EnumCheckbox } from "./SectionCheckbox";
import { P } from "theme/typography";
import theme, { EllipsisSX } from "theme";
import { CancerRecurrenceCheckbox } from "../LensHitFilterControls";
import CancerStageSelect from "components/CancerStageSelect";
import PCheckbox from "components/PCheckbox";
import { useFlags } from "launchdarkly-react-client-sdk";

const CheckboxSX = {
  mx: 0,
  display: "flex",
  overflow: "hidden",
  " .MuiTypography-root": EllipsisSX,
  " .MuiCheckbox-root": { p: 0.25 },
};

export interface ProfileBasedReviewControlProps {
  form: Form;
  filterState: LensHitFilterState;
  reviewConfig?: ReviewConfig;
  filterButton: ReactNode;
}
const ProfileBasedReviewControl = (props: ProfileBasedReviewControlProps) => {
  const { filterState, reviewConfig, filterButton, form } = props;
  const { recommendedOnlyFilter } = useFlags();

  const { currentField } = filterState;

  const sectionOptions = useAppSelector(selectSectionOptions);
  const cancerStageOptions = useAppSelector(selectReviewCurrentCancerStageOptions);

  const suggestedPageTypes: PageType[] = (reviewConfig?.suggestedPageTypes || []).filter(
    (pt) => sectionOptions.findIndex((opt) => opt.key === pt && opt.type === "page_type") !== -1
  );
  const suggestedClusterTypes: ClusterType[] = (reviewConfig?.suggestedClusterTypes || []).filter(
    (ct) => sectionOptions.findIndex((opt) => opt.key === ct && opt.type === "cluster_type") !== -1
  );
  const suggestedLabels: string[] = (reviewConfig?.suggestedLabelNames || []).filter(
    (l) => sectionOptions.findIndex((opt) => opt.key === l && opt.type === "label") !== -1
  );

  const showStage = reviewConfig?.showStage && nonEmpty(cancerStageOptions);
  const showRecurrence = reviewConfig?.showCancer && nonEmpty(cancerStageOptions);

  const suggestedFiltersCount =
    (suggestedPageTypes.length ? 1 : 0) +
    (suggestedClusterTypes.length ? 1 : 0) +
    (suggestedLabels.length ? 1 : 0) +
    (showStage ? 1 : 0) +
    (showRecurrence ? 1 : 0) +
    (reviewConfig?.hideNegated ? 1 : 0) +
    (reviewConfig?.showFamily ? 1 : 0) +
    (recommendedOnlyFilter ? 1 : 0);

  const updateFieldId = (id) => {
    filterState.clearFilters(true);
    filterState.setFormFieldId(id);
  };

  return (
    <Flex flexDirection="column" width="100%">
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          flexWrap: "wrap",
          rowGap: 1,
        }}>
        <FormFieldSelect
          fieldId={filterState.currentField?.id}
          changeFieldId={updateFieldId}
          allowNone={false}
          sx={{ mr: 1 }}
        />
        {currentField && form && (
          <FormFieldPopover form={form} fieldId={currentField.id} navigateToBlock={() => void 0} hideLightbulb />
        )}
        {filterButton}
      </Box>
      {suggestedFiltersCount > 0 && (
        <>
          <Box
            className="document-excerpt"
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${suggestedFiltersCount > 2 ? "3" : suggestedFiltersCount}, minmax(0, 1fr))`,
              gap: "8px 8px",
              mt: 2,
              p: 1,
              pt: 2,
              border: `1px solid ${theme.palette.grey[50]}`,
              position: "relative",
            }}>
            <P
              variant="overline"
              sx={{
                color: "grey.400",
                backgroundColor: "#fff",
                position: "absolute",
                left: 16,
                top: -8,
                display: "block",
                px: 1,
              }}>
              <Flex flexDirection="row" centered>
                <InfoIcon color="primary" sx={{ mr: 1 }} />
                <P color="primary">Suggested Filters</P>
              </Flex>
            </P>
            {reviewConfig?.showFamily && (
              <PCheckbox
                sx={CheckboxSX}
                label="Only Family Findings"
                // Checked if selectedClusterTypes is only family, toggles it
                checked={true}
                onChange={(_evt, checked) => filterState.setShowNegated(!checked)}
              />
            )}
            {reviewConfig?.hideNegated && (
              <PCheckbox
                sx={CheckboxSX}
                label="Hide Negated Findings"
                checked={!filterState.showNegated}
                onChange={(_evt, checked) => filterState.setShowNegated(!checked)}
              />
            )}
            {recommendedOnlyFilter && (
              <PCheckbox
                sx={CheckboxSX}
                label="Priority Medical Findings"
                checked={filterState.recommendedOnly}
                onChange={(_evt, checked) => filterState.setRecommendedOnly(checked)}
              />
            )}
            {suggestedPageTypes.map((pageType) => (
              <EnumCheckbox<PageType>
                sx={CheckboxSX}
                key={pageType}
                value={pageType}
                selectedValues={filterState.selectedPageTypes}
                display={(pt) => {
                  const opt = sectionOptions.find((opt) => opt.key === pt && opt.type === "page_type");
                  return `${displayPageType(pt)} (${opt?.count})`;
                }}
                setSelectedValues={filterState.setSelectedPageTypes}
              />
            ))}
            {suggestedClusterTypes.map((clusterType) => (
              <EnumCheckbox<ClusterType>
                sx={CheckboxSX}
                key={clusterType}
                value={clusterType}
                selectedValues={filterState.selectedClusterTypes}
                display={(ct) => {
                  const opt = sectionOptions.find((opt) => opt.key === ct && opt.type === "cluster_type");
                  return `${displayClusterType(ct)} (${opt?.count})`;
                }}
                setSelectedValues={filterState.setSelectedClusterTypes}
              />
            ))}
            {suggestedLabels.map((label) => (
              <EnumCheckbox<string>
                sx={CheckboxSX}
                key={label}
                value={label}
                selectedValues={filterState.selectedPageLabels}
                display={(l) => {
                  const opt = sectionOptions.find((opt) => opt.key === l && opt.type === "label");
                  return `${l} (${opt?.count})`;
                }}
                setSelectedValues={filterState.setSelectedPageLabels}
              />
            ))}
            {showRecurrence && <CancerRecurrenceCheckbox filterState={filterState} sx={CheckboxSX} />}
            {showStage && (
              <CancerStageSelect
                options={cancerStageOptions}
                value={filterState.selectedCancerStages}
                setValue={filterState.setSelectedCancerStages}
              />
            )}
          </Box>
        </>
      )}
    </Flex>
  );
};

export default ProfileBasedReviewControl;
